import { Box, makeStyles } from '@material-ui/core';
import React, { FC, useState } from 'react';
import { ChromePicker } from 'react-color';
import { useDispatch } from 'react-redux';
import useComponentVisible from 'src/hooks/useComponentVisible';
import { RootState, useSelector } from 'src/store';
import { editBgColor } from 'src/store/modules/imageUpload';
import ChevronUp from 'src/components/Icon/ChevronUp';
import ChevronDown from 'src/components/Icon/ChevronDown';
import BgPicker from '../BgPicker';
import { MultiBgImage } from '../EditMultiBgImage/EditMultiBgImage';
import { MultiBgColor } from '../EditMultiBgColor/EditMultiBgColor';

interface editTextProps {
  menuTitle?: string;
  type: string;
}

const useStyles = makeStyles(() => ({
  root: {
    padding: '1.5rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    border: '1px solid #D9D9D9'
  },
  fontBox: {
    width: '100%',
    height: '2.5rem',
    padding: '0.5rem',

    cursor: 'pointer',
    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400,

    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #D9D9D9'
  },
  input: {
    height: '4.5rem',
    padding: '0.5rem',
    outline: 'none',
    overflow: 'hidden',

    '&:focus': {
      border: 0,
      outline: 'none'
    },

    border: '1px solid #dddddd'
  },
  inputRoot: {
    padding: 0,
    height: '100%',
    display: 'block'
  },
  inputText: {
    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: '1.2rem',
    letterSpacing: '0.015rem'
  },

  bottomBtn: {
    padding: '0.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    border: '1px solid #dddddd',
    backgroundColor: '#F2F2F2'
  },
  btnMinusPlus: {
    maxWidth: '1.5rem',
    minWidth: '1.5rem',
    maxHeight: '1.5rem',
    minHeight: '1.5rem',
    padding: 0,
    display: 'flex'
  },

  fontNumber: {
    width: '3rem',
    height: '1.5rem',

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400,

    borderRadius: '0.25rem',
    border: '1px solid #D9D9D9',
    backgroundColor: '#fff'
  },

  buttonWrapper: {
    maxWidth: '1.5rem',
    minWidth: '1.5rem',
    maxHeight: '1.5rem',
    minHeight: '1.5rem'
  },
  buttonImage: {
    maxWidth: '1.5rem',
    minWidth: '1.5rem',
    maxHeight: '1.5rem',
    minHeight: '1.5rem'
  },

  boxWrapper: {
    // margin: '0 0.5rem',

    padding: '0.5rem',

    display: 'flex',
    justifyContent: 'space-between',
    border: '1px solid #ddd'
  },
  colorBox: {
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    border: '1px solid #D9D9D9',
    textAlign: 'center',

    color: '#000',
    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400
  },
  textAlign: {
    cursor: 'pointer'
  }
}));

const EditMultiBg: FC<editTextProps> = ({ menuTitle, type }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const currentIndex = useSelector(
    (state: RootState) => state.imageUpload.currentIndex
  );
  const bg = useSelector((state: RootState) => state.imageUpload.bg);
  const bgColor = useSelector((state: RootState) => state.imageUpload.bgColor);

  const {
    refPicker,
    refButton1,
    refButton2,
    isComponentVisible,
    setIsComponentVisible
  } = useComponentVisible(false);
  const [isCheck, setIsCheck] = useState(false);

  const handleColor = c => {
    const newColors = bgColor?.[type]?.slice();
    newColors.splice(currentIndex, 1, c.hex);
    const newColor = JSON.parse(
      `{"${type}": [${newColors?.map((_: any, idx: number) => {
        const newC = newColors?.[idx];
        return `"${newC}"`;
      })}]}`
    );
    dispatch(editBgColor({ ...bgColor, ...newColor }));
  };

  return (
    <div className={classes.root}>
      <div
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          fontFamily: 'Archivo',
          letterSpacing: '0.0175rem'
        }}
      >
        {menuTitle}
      </div>
      <div>
        <Box className={classes.boxWrapper}>
          {bg === 'image' ? (
            <MultiBgImage type={type} />
          ) : (
            <MultiBgColor
              type={type}
              picker={{
                refPicker,
                refButton1,
                refButton2,
                isComponentVisible,
                setIsComponentVisible
              }}
            />
          )}
          <Box
            style={{ cursor: 'pointer' }}
            onClick={() => setIsCheck(!isCheck)}
          >
            {isCheck ? (
              <ChevronUp
                style={{
                  width: '1.5rem',
                  height: '1.5rem'
                }}
              />
            ) : (
              <ChevronDown
                style={{
                  width: '1.5rem',
                  height: '1.5rem'
                }}
              />
            )}
          </Box>
        </Box>
        {isCheck && <BgPicker onClose={() => setIsCheck(!isCheck)} />}
        {isComponentVisible && (
          <div ref={refPicker} style={{ width: '100%', marginTop: '1rem' }}>
            <ChromePicker
              color={bgColor?.[type]?.[currentIndex]}
              onChange={handleColor}
              disableAlpha
              width="100%"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EditMultiBg;
